import { getBrand } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailer } from "@faire/help-center-utils/serialized-data/getRetailer";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { SearchInput } from "@faire/web--source/slate/SearchInput";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import Fuse from "fuse.js";
import debounce from "lodash/debounce";
import map from "lodash/map";
import pick from "lodash/pick";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { LinkedHelpCenterButton } from "../../components/HelpCenterButton/LinkedHelpCenterButton";
import { useClearNodesInLocalStorageOnLoad } from "../../hooks/useClearNodesInLocalStorageOnLoad";
import { useInstantAnswerContextAdditionalData } from "../../hooks/useInstantAnswerContextAdditionalData";
import { useMultipleNodesSetter } from "../../hooks/useMultipleNodesSetter";
import { useNavigation } from "../../hooks/useNavigation";
import { useSelectedNode } from "../../hooks/useSelectedNode";
import { useSelectedNodeAnswer } from "../../hooks/useSelectedNodeAnswer";
import { useSelfServeContext } from "../../hooks/useSelfServeContext";
import {
  InstantAnswerContext,
  ISearchableNode,
  SelfServeContext,
  SelfServeNode,
} from "../../types";
import { findPathsFromSelectedNode } from "../../utils/findPathsFromSelectedNode";

import { ConfigurableOptions } from "./ConfigurableOptions";
import { getAllVisibleNodes } from "./getAllVisibleNodes";

const initSearchOptions: Fuse.IFuseOptions<ISearchableNode> = {
  isCaseSensitive: false,
  shouldSort: false,
  includeScore: false,
  includeMatches: false,
  findAllMatches: false,
  useExtendedSearch: false,
  minMatchCharLength: 1,
  location: 0,
  threshold: 0.6,
  distance: 100,
  keys: ["label", "metaData"],
};

export const IssueSearch: React.FC = () => {
  useClearNodesInLocalStorageOnLoad();
  const { context } = useSelfServeContext();
  const { tree } = context;
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchOptions, setSearchOptions] =
    useState<Fuse.IFuseOptions<ISearchableNode>>(initSearchOptions);
  const { level1Node, level2Node, level3Node, level4Node, level5Node } =
    useSelectedNode();
  const [nodeIsSelected, setNodeIsSelected] = React.useState<boolean>(false);
  const [searchResult, setSearchResult] = React.useState<
    Fuse.FuseResult<ISearchableNode>[]
  >([]);
  const [nodes, setNodes] = useState<SelfServeNode[]>([]);
  const { instantAnswer } = useSelectedNodeAnswer();
  const { instantAnswerContextAdditionalData } =
    useInstantAnswerContextAdditionalData(instantAnswer);

  useEffect(() => {
    const instantAnswerContext: InstantAnswerContext = {
      brandOrder: context.selectedBrandOrder,
      brand: getBrand(),
      retailer: getRetailer(),
      shouldDisplayCTAOfContactBrandOrRetailer: true,
      ...instantAnswerContextAdditionalData,
    };

    async function getAllVisibleNodesAsync(context: SelfServeContext) {
      const allVisibleNodes = await getAllVisibleNodes(context);
      allVisibleNodes.map((node) => {
        const articleTitles = map(node.articles, (article) => article.name);
        const description =
          node.instantAnswer?.descriptionBuilder?.(instantAnswerContext);

        if (Array.isArray(description)) {
          node.metaData = [
            node.instantAnswer?.titleBuilder?.(instantAnswerContext) ?? "",
            ...((description as string[]) ?? ""),
            ...articleTitles,
          ].filter((metaData) => metaData !== "");
        } else {
          node.metaData = [
            node.instantAnswer?.titleBuilder?.(instantAnswerContext) ?? "",
            ...articleTitles,
          ].filter((metaData) => metaData !== "");
        }
      });
      setNodes(allVisibleNodes);
    }

    getAllVisibleNodesAsync(context);
  }, [context, instantAnswerContextAdditionalData]);

  const searchableNodes = map(nodes, (node) =>
    pick(node, ["id", "label", "metaData", "level"])
  );

  const fuseRef = useRef(new Fuse(searchableNodes, searchOptions));
  const { setMultipleNodes } = useMultipleNodesSetter();
  const { goToNextStep } = useNavigation();
  const goToNextStepRef = useRef(goToNextStep);

  goToNextStepRef.current = goToNextStep;

  const searchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value;
    debounce(() => setSearchResult(fuseRef.current.search(searchTerm)), 500)();
    setSearchValue(searchTerm);
  };

  useEffect(() => {
    fuseRef.current = new Fuse(searchableNodes, searchOptions);
  }, [searchOptions, searchableNodes]);

  useEffect(() => {
    if (
      (level1Node || level2Node || level3Node || level4Node || level5Node) &&
      nodeIsSelected
    ) {
      goToNextStepRef.current();
      setNodeIsSelected(false);
    }
  }, [
    level1Node,
    level2Node,
    level3Node,
    level4Node,
    level5Node,
    nodeIsSelected,
  ]);

  const handleHelpCenterButtonClick = (node: ISearchableNode) => {
    const selectedNode = tree[node.id];
    if (selectedNode) {
      const allPaths = findPathsFromSelectedNode(tree, selectedNode);
      setMultipleNodes(allPaths);
      setNodeIsSelected(true);
    }
  };

  return (
    <Column>
      <Row align="center">
        <Typography>
          <StrictLocalMsg defaultMessage="Search" />
        </Typography>
        <Spacer width="2x" />

        <SearchInputWrapper>
          <SearchInput
            value={searchValue}
            onChange={searchOnChange}
            onClear={() => setSearchValue("")}
            placeholder="Search issues or topics"
          />
        </SearchInputWrapper>
      </Row>
      <Spacer height="1x" />
      <ConfigurableOptions
        searchOptions={searchOptions}
        setSearchOptions={setSearchOptions}
      />
      {searchResult.map(({ item }) => {
        return (
          <LinkedHelpCenterButton
            searchableNode={item}
            key={item.id}
            onClick={() => handleHelpCenterButtonClick(item)}
          />
        );
      })}
    </Column>
  );
};

const SearchInputWrapper = styled.div`
  width: 100%;
`;
